<script lang="ts" setup>
    import { MODELS, modelLabelKey } from './constants'
    import PkLanguageModel from '../chat/PkLanguageModel.vue'
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'

    const { t: $t, te: $te } = useI18n({
        useScope: 'global',
    })

    const props = defineProps<{
        name?: string
        multiple?: boolean
        excludeModels?: string[]
    }>()

    const models = computed(() => {
        const excluded = props.excludeModels ?? []
        return MODELS.filter((m) => !excluded.includes(m)).map((model) => ({
            label: $te(modelLabelKey(model)) ? $t(modelLabelKey(model)) : model,
            value: model,
        }))
    })

    type ModelOption = (typeof models.value)[number]

    // The default filter only looks at the label: match the raw id too (e.g.
    // "openai/gpt-5.6-sol", the identifier used by the API), so that a provider
    // name or a version written with dashes finds its models.
    const searchModels = (query: string, options: ModelOption[]) => {
        const terms = query.toLowerCase().split(/\s+/).filter(Boolean)
        if (!terms.length) {
            return options
        }
        return options.filter((option) => {
            const haystack = `${option.label} ${option.value}`.toLowerCase()
            return terms.every((term) => haystack.includes(term))
        })
    }
</script>

<template>
    <VvCombobox
        :options="models"
        :multiple
        :badges="multiple"
        :name
        searchable
        :search-function="searchModels">
        <template #value="{ selectedOptions }">
            <template v-if="props.multiple">
                <PkLanguageModel
                    v-for="option in selectedOptions"
                    :key="option.value"
                    :model-name="option.value"
                    :class="{
                        'text-12 gap-4 border border-surface-3 bg-surface px-6 py-3 rounded-md':
                            selectedOptions.length > 1,
                        'text-14 gap-sm': selectedOptions.length === 1,
                    }" />
            </template>
            <PkLanguageModel
                v-else
                :model-name="selectedOptions[0]?.value"
                class="text-14 gap-sm" />
        </template>
        <template #option="{ option }">
            <PkLanguageModel
                :model-name="option.value"
                class="text-14 gap-sm" />
        </template>
    </VvCombobox>
</template>
